import { BankType, BrandType, NegotiationOption, NegotiationOptionAlternative, NegotiationOptionAlternativeType, NegotiationType, Normative026, OutDebt, OutDebtFieldsFragment, OutDebtState } from '../generated/graphql';
import { getNegBooleans } from './getNegBooleans';

export const getNormative026ContentKey = (
  negOption: NegotiationOption,
  outDebt: OutDebt,
  isConsequenceText?: boolean,
  alternative?: NegotiationOptionAlternative,
  allOutDebts: OutDebtFieldsFragment[] = [],
) => {
  const normative026Raw = outDebt.extra?.normative026;

  const {
    isScotiabank, isDav, isBogota, isRefinance, isTotalPayment,
    isBalanceBehindPayment, isBbva, isTranqui,
  } = getNegBooleans(outDebt, negOption);

  if (isBogota && (isRefinance || isBalanceBehindPayment)) return null;
  if (isScotiabank && isRefinance && !isConsequenceText) return null;
  if (!isDav && isTotalPayment) return null;

  const overwriteToRestructure = [ Normative026.Modification, Normative026.Refinance ]
    .includes(normative026Raw as Normative026)
    && normative026Raw === Normative026.Refinance
    && (outDebt?.daysBehind || 0) > 60
    && !isScotiabank
    && !isDav;

  // For BBVA, take into account the alternative type istead of this disgusting spaghetti
  const overwriteToRestructureBbva = isBbva && isTranqui && isRefinance
    && negOption.alternatives[0].type === NegotiationOptionAlternativeType.Restructuring;

  const normative026 = (overwriteToRestructure || overwriteToRestructureBbva)
    ? Normative026.Restructuring
    : outDebt.extra?.normative026;

  const hasWeightedRateSubKey = isBogota && !!outDebt.extra?.maxTerm ? '_weighted_rate' : '';

  const negTypeNormative026 = (negOption.type === NegotiationType.Normalization
    || negOption.type === NegotiationType.Refinance
    || negOption.type === NegotiationType.Modification
    || negOption.type === NegotiationType.Restructure
    || negOption.type === NegotiationType.PenaltyPortfolioPaymentAgreement
    || negOption.type === NegotiationType.TotalPayment);
  const isNormative026 = !!normative026;

  const isPunished = outDebt.extra?.state === OutDebtState.Punished;
  if (!negTypeNormative026 && !isConsequenceText) return null;

  if (!isNormative026) return null;

  const getBlockedBulletKey = () => {
    if (isBogota && !!outDebt.daysBehind && isRefinance) {
      if (outDebt.daysBehind < 60) { return '_no_blocked'; } if (outDebt.daysBehind > 60) return '_blocked';
    }
    return '';
  };

  const isMulti = allOutDebts
    .filter(({ bank, brand }) => bank === BankType.Bbva && brand === BrandType.Tranquiapp)
    .length > 1;
  const multiSubkey = isMulti ? '_multi' : '';

  const normative026Subkey = normative026 !== Normative026.Refinance ? `_${normative026}` : '';

  // BBVA PY-3396: extracupo es pre-calculado por backend y entregado en cada
  // NegotiationOptionAlternative que aplique REFINANCE/RESTRUCTURING con extracupo.
  // MODIFICACIÓN excluida: backend nunca popula extracupo en este caso.
  // Si el caller no pasa alternative, fallback a la primera alternativa del grupo.
  const extracupoSource = alternative ?? negOption.alternatives[0];
  const extracupoSubkey = (
    isBbva && isRefinance
    && extracupoSource?.extracupo != null
    && extracupoSource.extracupo > 0
    && normative026 !== Normative026.Modification
  ) ? '_extracupo' : '';

  if (isBogota && isRefinance) return `negotiation_${negOption.type}${normative026Subkey}_consequences${getBlockedBulletKey()}${hasWeightedRateSubKey}`;

  const blockTypeSubkey = (isScotiabank && isRefinance && outDebt.extra?.blockType) ? `_${outDebt.extra.blockType}` : '';
  const c25Subkey = (isScotiabank && isRefinance && outDebt.extra?.isC25) ? '_C25' : '';

  const isRappiPay = outDebt.bank === BankType.Rappipay;
  const isRappiPrestamo = outDebt.bank === BankType.RappiPrestamo;
  const isRestructure = negOption.type === NegotiationType.Restructure;
  const fallenSubkey = outDebt?.extra?.isFallen ? '_fallen' : '';
  if (isConsequenceText) {
    switch (negOption.type) {
      case NegotiationType.PenaltyPortfolioPaymentAgreement:
        return `negotiation_${negOption.type}${isPunished ? `_${OutDebtState.Punished}` : ''}_consequences`;
      case NegotiationType.TotalPayment:
      case NegotiationType.Refinance:
      case NegotiationType.Normalization:
      case NegotiationType.Restructure:
        // For davivienda, the penalty texts have relevance over normative.
        if (isDav && isPunished) return `negotiation_${negOption.type}_${OutDebtState.Punished}_consequences`;
        if (isNormative026) return `negotiation_${negOption.type}${normative026Subkey}${multiSubkey}${blockTypeSubkey}${c25Subkey}_consequences${extracupoSubkey}`;
        break;
      default:
        return `negotiation_${negOption.type}_consequences${fallenSubkey}${extracupoSubkey}`;
    }
    if ((isRappiPay || isRappiPrestamo) && isRestructure) return `negotiation_${negOption.type}_consequences${fallenSubkey}${extracupoSubkey}`;
    return `negotiation_${negOption.type}${isPunished ? `_${OutDebtState.Punished}` : ''}_consequences${extracupoSubkey}`;
  }

  switch (negOption.type) {
    case NegotiationType.PenaltyPortfolioPaymentAgreement:
      return 'normative026_PUNISHED_PenaltyPortfolioPaymentAgreement';
    case NegotiationType.TotalPayment:
    case NegotiationType.Refinance:
    case NegotiationType.Normalization:
    case NegotiationType.Restructure:
      // For davivienda, the penalty texts have relevance over normative.
      if (isDav && isPunished) return `negotiation_${negOption.type}_${OutDebtState.Punished}_consequences`;
      if (isNormative026) return `negotiation_${negOption.type}${normative026Subkey}${multiSubkey}_consequences${extracupoSubkey}`;
      return null;
    default:
      return null;
  }
};
